import styled from '@emotion/styled';
import { Typography } from 'antd';
import type { ReactElement, ReactNode } from 'react';
import React from 'react';
import tw from 'twin.macro';

import useBelowWidth from '@/hooks/useBelowWidth';

interface Props {
  title?: string;
  column?: number;
  children: ReactNode;
}

const Tescriptions = ({ title, column = 1, children }: Props) => {
  const max768 = useBelowWidth(768);
  const COLUMN = max768 ? 1 : column;

  const getCorrectedNodes = () => {
    let currentSpanCount = 0;

    return React.Children.toArray(children)
      .filter((child) => {
        return React.isValidElement<ItemProps>(child);
      })
      .map((c, index, array) => {
        const child = c as ReactElement<ItemProps>;
        let span = child.props.span ?? 1; // Tescriptions.Item.span이 옵셔널값이기 때문에 기본값 지정이 필요함
        const nextChild =
          index + 1 >= array.length
            ? undefined
            : (array[index + 1] as ReactElement<ItemProps>);
        const nextSpan = nextChild ? (nextChild.props.span ?? 1) : undefined;

        if (span > COLUMN) {
          // span이 COLUMN 넘지 않도록 보정
          span = COLUMN;
        }

        if (currentSpanCount >= COLUMN) {
          // 현재 행에 공간이 없다면 새로운 행으로 계산하기 위해 초기화
          currentSpanCount = 0;
        }

        if (
          nextSpan
            ? currentSpanCount + span !== COLUMN && // 현재 행에 추가했을때 정확히 맞아 떨어지지 않고
              currentSpanCount + span + nextSpan > COLUMN // 현재 행에 다음 노드까지 추가했을 때 column을 넘는다면
            : currentSpanCount + span < COLUMN // 현재 행에 추가해도 공간이 남는다면
        ) {
          span += COLUMN - (currentSpanCount + span); // 현재 노드를 차지 가능한 최대 span으로 보정 (다음 노드는 다음 반복문에서 처리될거니까 현재 반복문에서는 건드리지 않음)
        }

        const isFirstOfRow = currentSpanCount > 0;
        currentSpanCount += span;

        return {
          ...child,
          props: {
            ...child.props,
            _isFirstOfRow: isFirstOfRow,
            span,
          },
        };
      });
  };

  return (
    <div css={tw`flex flex-col items-start gap-[8px] w-full min-w-[480px]`}>
      {title && (
        <Typography.Text css={tw`font-semibold leading-[22px] text-[14px]`}>
          {title}
        </Typography.Text>
      )}
      <Wrapper $column={COLUMN}>{getCorrectedNodes()}</Wrapper>
    </div>
  );
};

interface ItemProps {
  label: ReactNode;
  required?: boolean;
  span?: number;
  _isFirstOfRow?: boolean; // Tescription 단에서 조작할 값이라 옵셔널
  children: ReactNode;
}

const TescriptionItem = ({
  label,
  required,
  span = 1,
  _isFirstOfRow,
  children,
}: ItemProps) => {
  return (
    <Item $span={span} $isFirstOfRow={_isFirstOfRow}>
      <dl>
        <dt>
          <div>
            {label}
            <span css={tw`text-[#1677ff]`}>{required && '*'}</span>
          </div>
        </dt>
        <dd>{children}</dd>
      </dl>
    </Item>
  );
};

Tescriptions.Item = TescriptionItem;

export default Tescriptions;

const Wrapper = styled.ul<{ $column?: number }>`
  ${tw`grid w-full`}

  grid-template-columns: repeat(${({ $column }) => $column}, 1fr);
`;

const Item = styled.li<{ $span?: number; $isFirstOfRow?: boolean }>`
  ${tw`w-full m-[-1px 0 0 0]`}

  grid-column: ${({ $span }) =>
    !!$span && $span > 1 ? `span ${$span}` : 'auto'};
  border-top: 1px solid #f0f0f0;
  border-bottom: 1px solid #f0f0f0;

  dl {
    ${tw`flex flex-row items-center h-full`}

    dt {
      ${tw`flex items-center p-[16px 24px] w-[160px] h-full`}
      ${tw`leading-[22px] text-[14px] font-semibold text-[rgba(0, 0, 0, 0.88)] bg-[#fafafa] whitespace-nowrap`}
      border-right:1px solid #f0f0f0;
      border-left: ${({ $isFirstOfRow }) =>
        $isFirstOfRow ? '1px solid #f0f0f0' : 'unset'};

      div {
        ${tw`inline-block`}
      }
    }

    dd {
      ${tw`flex-[1] flex flex-row items-center p-[7px 16px] h-full break-all whitespace-pre-wrap`}

      & .ant-form-item {
        ${tw`w-full m-0`}
      }
    }
  }
`;
